import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';

type TechnicalRecordArchiveParam = 'all' | 'true' | 'false';

@Injectable({
  providedIn: 'root',
})
export class TechnicalViewService {
  activeVehicle: any = {};

  public technicalRecords$ = new BehaviorSubject<any[]>([]);
  public technicalRecordsObservable = this.technicalRecords$.asObservable();

  private reloadRecordsSource = new Subject<void>();
  reloadRecords$ = this.reloadRecordsSource.asObservable();

  triggerReload() {
    this.reloadRecordsSource.next();
  }

  constructor(
    private router: Router,
    private api: ApiService,
    private fb: FormBuilder,
    private http: HttpClient
  ) {}

  setActiveVehicle($event: any) {
    this.activeVehicle = $event;
    this.router.navigate(['/technical']);
  }

  updateTechnicalRecords(technicalRecords: any[]) {
    this.technicalRecords$.next(technicalRecords);
  }

  getTechnicalRecords(
    page: number,
    rows: number,
    keyword?: string | null,
    filters?: string[],
    isArchived: TechnicalRecordArchiveParam = 'all'
  ) {
    let params = new HttpParams()
      .set('skip', (page - 1) * rows)
      .set('limit', rows)
      .set('is_archived', isArchived);
    if (keyword?.trim()) {
      params = params.set('q', keyword.trim());
    }
    filters?.forEach((f) => {
      params = params.append('filter', f);
    });
    return this.api.getAPI('technicalrecords', params);
  }

  downloadTechnicalRecords(
    keyword?: string | null,
    filters?: string[],
    format: 'xlsx' | 'csv' = 'xlsx',
    isArchived: TechnicalRecordArchiveParam = 'all'
  ) {
    let params = new HttpParams()
      .set('format', format)
      .set('is_archived', isArchived);
    if (keyword?.trim()) {
      params = params.set('q', keyword.trim());
    }
    filters?.forEach((f) => {
      params = params.append('filter', f);
    });
    return this.api.getFileAPI('technicalrecords/download', params);
  }

  getTechnicalRecordsByVehicleId(
    vehicleId: string,
    page: number,
    rows: number,
    keyword?: string | null
  ) {
    let params = new HttpParams()
      .set('skip', (page - 1) * rows)
      .set('limit', rows);
    // The endpoint searches on `q` (record text and job lines); it never had
    // a `filter` param, so the search box was previously a no-op on a truck.
    if (keyword) {
      params = params.set('q', keyword);
    }
    return this.api.getAPI(`technicalrecords/vehicles/${vehicleId}`, params);
  }

  createTechnicalRecord(technicalRecord: any) {
    return this.api.postAPI('technicalrecords', technicalRecord);
  }

  updateTechnicalRecord(technicalRecord: any, id: number) {
    return this.api.patchAPI(
      'technicalrecords/update-record',
      technicalRecord,
      id
    );
  }

  updateTechnicalRecordJobs(technicalRecord: any, id: number) {
    return this.api.patchAPI('technicalrecordjobs/jobs', technicalRecord, id);
  }

  getJobByTechnicalRecordId(technicalRecordId: number) {
    return this.api.getAPI(`technicalrecords/jobs/${technicalRecordId}`);
  }

  deleteTechnicalRecord(id: number) {
    return this.api.deleteAPI('technicalrecords/delete-record', id);
  }

  deleteTechnicalJob(id: number) {
    return this.api.deleteAPI('technicalrecords/delete-job', id);
  }

  createTechnicalJob(technicalJob: any) {
    return this.api.postAPI('technicalrecords/jobs', technicalJob);
  }

  getTechnicalJobForm() {
    return this.fb.group({
      job_description: [''],
      remarks: [''],
      id: [''],
    });
  }

  jobForm = new FormGroup({
    job_description: new FormControl(''),
    remarks: this.fb.array([
      this.fb.group({
        job_id: new FormControl(''),
        remarks: new FormControl(''),
      }),
    ]),
    id: new FormControl(''),
  });

  technicalForm = new FormGroup({
    vehicle_reference: new FormControl(''),
    vehicle_id: new FormControl(''),
    chassis_no: new FormControl(''),
    odometer: new FormControl(''),
    service_jobno: new FormControl(''),
    service_brakes: new FormControl(''),
    remarks: new FormControl(''),
    record_type: new FormControl(''),
    date_completed: new FormControl(''),
    service_type: new FormControl(''),
    tyre_pos: new FormControl(''),
    tyre_size: new FormControl(''),
    tyre_type: new FormControl(''),
    technical_record_jobs: new FormArray([]),
  });
}
